import PauseOverlay from './PauseOverlay.js';
import DateTime from './DateTime.js';
import Media from './Media.js';
import Ui from './Ui.js';
import Logger from './Logger.js';
import Analytics from './analytics/Analytics.js';
import Stream from './Stream.js';
import Uint8ArrayUtils from './Uint8ArrayUtils.js';
import CastQueue from './CastQueue.js';
import MimeResolver from './MimeResolver.js';
import config from './config/config.js';
import Configuration from './config/Configuration.js';
// import BitrateLogger from './BitrateLogger.js';

/* global shaka */

const DEBUG_LEVEL = config.environment === 'DEVELOPMENT' ? Logger.levelTypes.DEBUG : Logger.levelTypes.NONE;

let isShakaPreset = false;

class Receiver {
    constructor(options, logger, ui) {
        this.options = options;
        this.logger = logger;
        this.ui = ui;
        this.logger.info(`[Receiver] constructor, version: ${config.version}, env: ${config.environment}`);

        this.mimeResolver = new MimeResolver(logger);
        this.context = cast.framework.CastReceiverContext.getInstance();
        this.receiverOption = new cast.framework.CastReceiverOptions(); // TODO: looks like useless variable
        this.receiverOption.useLegacyDashSupport = false;
        this.playerManager = this.context.getPlayerManager();
        this.breakManager = this.playerManager.getBreakManager();

        this.initCafLoggerLevel();

        this.onLoadMessage = this.onLoadMessage.bind(this);

        this.hasPauseOverlay = false;
        this.dateTime = new DateTime(document.getElementById('time'));
        this.initEvents();
        this.initMessageInterceptors();

        this.analytics = null;
        // this.bitrateLogger = new BitrateLogger(this.playerManager, this.logger);
        this.context.start({ queue: new CastQueue(logger) });
    }

    /**
     * To set CAF logger level. For development purpose ONLY
     */
    initCafLoggerLevel() {
        if (this.logger.getLevel() > Logger.levelTypes.NONE) {
            this.context.setLoggerLevel(cast.framework.LoggerLevel.DEBUG);
            this.initCoreEventsListener();
        }
    }

    /**
     * Listen to all Core Events. For development purpose ONLY
     */
    initCoreEventsListener() {
        const categoryTypes = cast.framework.events.category;
        this.playerManager.addEventListener(categoryTypes.CORE, (event) => {
            this.logger.debug(event);
        });
        this.playerManager.addEventListener(categoryTypes.DEBUG, (event) => {
            this.logger.debug(event);
        });
    }

    initEvents() {
        this.playerManager.addEventListener(
            cast.framework.events.EventType.PAUSE,
            (event) => {
                if (this.hasPauseOverlay && !event.ended) {
                    this.logger.info('PAUSE EVENT - Show pause overlay');
                    PauseOverlay.show();
                }
            }
        );
        this.playerManager.addEventListener(
            cast.framework.events.EventType.PLAY,
            () => {
                if (this.hasPauseOverlay) {
                    this.logger.info('PLAY EVENT - Hide pause overlay');
                    PauseOverlay.hide();
                }
            }
        );

        // When in queue mode, it's not executing the play and pause events
        this.playerManager.addEventListener(
            cast.framework.events.EventType.MEDIA_STATUS,
            (event) => {
                this.logger.info('Media Status Event', event);
                if (this.hasPauseOverlay) {
                    if (event.mediaStatus.playerState === 'PAUSED') {
                        this.logger.info('MEDIA STATUS PAUSE - Show pause overlay');
                        PauseOverlay.show();
                    } else if (event.mediaStatus.playerState === 'PLAYING' || event.mediaStatus.playerState === 'IDLE') {
                        this.logger.info('MEDIA STATUS PLAYING - Hide pause overlay');
                        PauseOverlay.hide();
                    }
                }
            }
        );

        // closed captions handling (subtitles)
        this.playerManager.addEventListener(
            cast.framework.events.EventType.PLAYER_LOAD_COMPLETE,
            (request) => {
                this.textTracksRebuild(request);
            }
        );

        this.playerManager.addEventListener(
            cast.framework.events.EventType.ERROR,
            (event) => {
              console.warn('ERROR', event);
            }
        );

        this.context.addCustomMessageListener(Configuration.get('customChannel'),
            (customEvent) => {
                this.logger.info('Got a Custom event');
                this.logger.debug('Event data', customEvent.data);
                if (Object.prototype.hasOwnProperty.call(customEvent.data, 'ui')) {
                    this.logger.info('Initialize UI');
                    this.ui.update(customEvent.data.ui);
                }
                this.logger.info(customEvent);
                if (Object.prototype.hasOwnProperty.call(customEvent.data, 'analytics')) {
                    if (!this.analytics) {
                        this.analytics = new Analytics(
                            customEvent.data.analytics,
                            this.logger,
                            Configuration.get('collectDomain'),
                            Configuration.get('collectDomainSSL')
                        );
                    }
                }
            });

        this.context.addEventListener(
            cast.framework.system.EventType.READY,
            () => {
                this.logger.debug('[Receiver] system ready event');
            }
        );
    }

    initMessageInterceptors() {
        this.playerManager.setMessageInterceptor(
            cast.framework.messages.MessageType.LOAD,
            this.onLoadMessage
        );
    }

    /**
     * @param {cast.framework.messages.LoadRequestData} loadRequestData
     * @return {promise | cast.framework.messages.LoadRequestData}
     */
    onLoadMessage(loadRequestData) {
        this.logger.info(`[Receiver] onLoadMessage, id: ${loadRequestData.requestId}`);
        this.logger.info(JSON.stringify(loadRequestData));
        if (loadRequestData.media && loadRequestData.media.contentId) {

            if (loadRequestData.media.streamType === cast.framework.messages.StreamType.LIVE) {
                loadRequestData.currentTime = undefined; // hotfix for Android client sending currentTime = 0
            }

            Media.load(loadRequestData.media.contentId);
            this.logger.info(`DRM config: ${Configuration.get('drmType')}`);
            this.logger.debug('loadRequestData', loadRequestData);
            const customData = this.getCustomData(loadRequestData);
            this.hasPauseOverlay = PauseOverlay.init(customData);
            if (this.analytics) {
                this.analytics.setCustomData(customData);
            }

            // if it starts with http(s) it's not a media id and can't get smill
            // file with license info etc.
            const mediaContentId = loadRequestData.media.contentId;
            if (mediaContentId.startsWith('http://') || mediaContentId.startsWith('https://')) {
                return this.setMimeType(loadRequestData.media).then(() => loadRequestData);
            }

            const streamOptions = {
                authToken: customData.authToken,
                mediaId: loadRequestData.media.contentId,
                streamType: loadRequestData.media.streamType
            };

            const streamInfo = Stream.getStream(streamOptions, this.logger);

            return streamInfo.call().then((stream) => {
                this.logger.info('In promise for stream info');
                this.logger.debug('Stream', stream);
                loadRequestData.media.contentUrl = stream.stream;

                return this.setMimeType(loadRequestData.media)
                    .then(waitForShakaIsPreset)
                    .then(() => {
                        this.logger.info('setMediaPlaybackInfoHandler');
                        this.playerManager.setMediaPlaybackInfoHandler(
                            (loadRequest, playbackConfig) => {
                                this.logger.info('In setMediaPlaybackInfoHandler');
                                if (stream.licenseUrl) {
                                    playbackConfig.licenseUrl = stream.licenseUrl;
                                    this.logger.info(`Set ${Configuration.get('drmType')} licenseUrl on playbackConfig: ${stream.licenseUrl}`);
                                    switch (Configuration.get('drmType')) {
                                        case 'playready':
                                            playbackConfig.protectionSystem = cast.framework.ContentProtection.PLAYREADY;
                                            break;
                                        case 'clearkey':
                                            playbackConfig.protectionSystem = cast.framework.ContentProtection.CLEARKEY;
                                            break;
                                        case 'widevine':
                                            playbackConfig.licenseRequestHandler = (networkRequestInfo) => {
                                                this.logger.info('WideVine LicenseRequest Handler');
                                                const widevineChallenge = Uint8ArrayUtils.toBase64(networkRequestInfo.content);
                                                const content = {
                                                    getWidevineLicense: {
                                                        releasePid: stream.releasePid,
                                                        widevineChallenge
                                                    }
                                                };
                                                // Replace content with a json string for the license server
                                                networkRequestInfo.content = JSON.stringify(content);
                                                this.logger.info(`New WideVine license uri: ${networkRequestInfo.url}`);
                                            };
                                            playbackConfig.licenseHandler = (arr) => {
                                                this.logger.info('WideVince LicenseHandler');
                                                // Convert uint8array to string and parse json
                                                const jsonMsg = Uint8ArrayUtils.toString(arr);
                                                const license = JSON.parse(jsonMsg);
                                                // Return Uint8Array from the license in the json
                                                return Uint8ArrayUtils.fromBase64(license.getWidevineLicenseResponse.license);
                                            };
                                            playbackConfig.protectionSystem = cast.framework.ContentProtection.WIDEVINE;
                                            break;
                                        default:
                                            playbackConfig.protectionSystem = cast.framework.ContentProtection.NONE;
                                            break;
                                    }
                                } else {
                                    this.logger.info('No license url found');
                                }
                                return playbackConfig;
                            }
                        );
                        
                        // if (stream.adTagUrl) {
                        //     loadRequestData.media.vmapAdsRequest = {
                        //         adTagUrl: stream.adTagUrl
                        //     };
                        // }
                        
                        return loadRequestData;
                    });
            });
        }
        return loadRequestData;
    }

    setMimeType(mediaInfo) {
        this.logger.info('[Receiver] setMimeType');
        let url = mediaInfo.contentUrl;
        if (!url) {
            url = mediaInfo.contentId;
        }
        const promise = this.mimeResolver.getMimeType(url);
        promise.then((mimetype) => {
            this.logger.debug(`[Receiver] finished setMimeType, mimetype: ${mimetype}`);
            // switching off for the non-functional android app
            return mediaInfo;
            /*
            mediaInfo.contentType = mimetype;
            if (['application/x-mpegURL', 'application/dash+xml'].indexOf(mediaInfo.contentType) > -1) {
                mediaInfo.metadata.metadataType = cast.framework.messages.GenericMediaMetadata;
                mediaInfo.streamType = cast.framework.messages.StreamType.LIVE;
            } else if (mediaInfo.contentType.match(/^audio/gi)) {
                mediaInfo.metadata.metadataType = cast.framework.messages.MusicTrackMediaMetadata;
                mediaInfo.streamType = cast.framework.messages.StreamType.BUFFERED;
            } else {
                mediaInfo.metadata.metadataType = cast.framework.messages.MovieMediaMetadata;
                mediaInfo.streamType = cast.framework.messages.StreamType.BUFFERED;
            }

            return mediaInfo;
            */
        });
        return promise;
    }

    /**
     * Fetch custom data from the loadRequestData, for some reason
     * the customdata is in different places when
     * using the queue or single media item.
     * @param {!cast.framework.messages.LoadRequestData} loadRequestData
     * @returns {Object}
     */
    getCustomData(loadRequestData) {
        if (typeof (loadRequestData.queueData) !== 'undefined') {
            if (typeof (loadRequestData.customData) !== 'undefined') {
                return loadRequestData.customData;
            }
            this.logger.warning('No custom data found in queue loadRequestData?');
            return {};
        }

        if (typeof (loadRequestData.media.customData) !== 'undefined') {
            return loadRequestData.media.customData;
        }
        this.logger.warning('No custom data found in NON queue loadRequestData?');
        return {};
    }

    /**
     * Modifing tracks data, especialy for mpeg-dash stream
     * @param {Object} request object of requested data
     * @returns {void}
     */
    textTracksRebuild(request) {
        // only for dash
        if (request.media.contentType !== 'application/dash+xml') {
            return;
        }

        const newTextTracksManager = this.playerManager.getTextTracksManager();
        const { tracks } = request.media;

        let track = {};
        let idAudio = 0;
        let idText = 0;
        let id = '';
        for (let i = 0; i < tracks.length; i += 1) {
            track = tracks[i];
            id = '';
            if (track.type === 'AUDIO') {
                idAudio += 1;
                id = idAudio;
            }
            if (track.type === 'TEXT') {
                idText += 1;
                id = idText;
            }
            track.name = track.name || `Track ${id}`;
            delete track.subtype;
        }
    }
}


function waitingForShakaIsLoaded() {
    console.log('[waitingForShakaIsLoaded] called');
    return new Promise((resolve) => {
        const timer = setInterval(() => {
            if (typeof shaka !== 'undefined') {
                console.log('[waitingForShakaIsLoaded] loaded');
                clearInterval(timer);
                resolve();
            }
        }, 100);
    });
}

function waitForShakaIsPreset() {
    console.log('[waitForShakaIsPreset] called');
    return new Promise((resolve) => {
        if (isShakaPreset) {
            resolve();
        } else {
            waitingForShakaIsLoaded()
                .then(() => {
                    // [PRJ0930RTE-1103] added special MPEG-DASH MIME type of Ads media files used in Google VAST
                    shaka.media.ManifestParser.registerParserByMime('video/vnd.mpeg.dash.mpd', shaka.dash.DashParser);
                })
                .catch((e) => { console.error(e); })
                .finally(() => {
                    isShakaPreset = true;
                    resolve();
                });
        }
    });
}

Configuration.loadConfig()
    .then(() => {
        const logger = new Logger({ debugLevel: DEBUG_LEVEL });
        const ui = new Ui(logger);
        const receiver = new Receiver(config, logger, ui);
    });
